import React, { useEffect, useMemo, useState } from "react";
import { Condition, Platform, Slot } from "../../../models";
import { Banner, Typography } from "@reconvert/react-ui-component";
import { Box } from "@chakra-ui/react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { getToken } from "../../../services/auth.service";
import { selectAppState, setIFrameLink } from "../../../app/slice/appSlice";
import { APP_PLATFORM, WIDGET_EDITOR_URL } from "../../../config";
import { useTranslation } from "react-i18next";
import { selectConditionsState } from "../../../app/slice/conditionSlice";
import { WIX_SITE_TYPE } from "../../../utils/constant";

interface SlotBannerProps {
  slots: Slot[];
  selectedCondition: Condition | undefined;
}

const SlotBanner: React.FC<SlotBannerProps> = ({ slots, selectedCondition }) => {
  const [inactiveSlots, setInactiveSlots] = useState<Slot[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { wixSiteType } = useAppSelector(selectConditionsState);
  const { storeInfo } = useAppSelector(selectAppState);

  useEffect(() => {
    const filtered = slots.filter(
      slot =>
        (!slot?.placementStatus
          ? !slot?.placement || slot?.placement === ""
          : !slot?.placementStatus)
    );

    setInactiveSlots(filtered);
    setCurrentIndex(0); // Reset index
  }, [slots]);

  const currentSlot = inactiveSlots[currentIndex];
  const matchingVariant = useMemo(() => {
    if (!selectedCondition?.variants || !currentSlot) return null;

    return selectedCondition.variants.find(variant => variant.slotId === currentSlot.id);
  }, [selectedCondition?.variants, currentSlot]);

  if (inactiveSlots.length === 0) return null;

  if (slots.length === 0) return null;

  const handlePlaceSlot = async () => {
    const tokenRes = await getToken(selectedCondition?.platformStoreId!);

    if (tokenRes.response?.token) {
      dispatch(
        setIFrameLink(
          `${WIDGET_EDITOR_URL}?variant=${matchingVariant?.id}${selectedCondition?.location! ? `&type=${selectedCondition?.location!}` : ""
          }&platformStoreId=${selectedCondition?.platformStoreId}&token=${tokenRes.response.token}${APP_PLATFORM === Platform.Wix ? "&isNewVariant=true" : ""}&conditionId=${matchingVariant?.conditionId}&slotId=${matchingVariant?.slotId}&slotNo=${matchingVariant?.slotNo}&openSlotPreview=true`,
        ),
      );
    }
  };

  const handleEditorRedirect = () => {
    const editorUrl = `https://manage.wix.com/editor/${storeInfo?.siteId}`;

    window.open(editorUrl, "_blank");
  };


  return (
    <Box>
      <Banner
        title={`Important! ${currentSlot?.name} is not placed yet`}
        status={"warning" as any}
        description={wixSiteType === WIX_SITE_TYPE.WIX_EDITOR ? t("In order to place the widget on the actual page, please go to the Wix Editor and place the widget") : t(
          "In order to enable this slot, please choose the slot location. Else this slot will be hidden on your product page.",
        )}
        primaryButton={{
          label: wixSiteType === WIX_SITE_TYPE.WIX_EDITOR ? t("Place slot on Wix Editor") : t("Place slot"),
          onClick: wixSiteType === WIX_SITE_TYPE.WIX_EDITOR ? handleEditorRedirect : handlePlaceSlot,
          variant: "secondary",
        }}
      />
    </Box>
  );
};

export default SlotBanner;
